/* eslint-disable max-lines */
import React from 'react';
import { Session } from '@utils/types/editors/sessions.ts';
import { DocumentResult } from '@utils/types/editors/generic.ts';
import { StartStopWord } from '@utils/types/editors/utils.ts';
import {
  DocumentTranscriptTextV1,
  DocumentTranscriptV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { Comment } from '@utils/types/comments/comments.ts';
import { Descendant } from 'slate';
import { ReactEditor } from 'slate-react';

export interface SharedTranscriptContextType {
  session?: Session;
  token?: string;
  document?: DocumentTranscriptV1;
  documentResult?: DocumentResult;
  search: string;
  caseSensitive: boolean;
  wholeWord: boolean;
  currentMatch: DocumentTranscriptTextV1 | null;
  startStopWord?: StartStopWord;
  popoverOpen?: boolean;
  showInfo: boolean;
  clickedComment?: Comment | null;
  showJumpButton: 'up' | 'down' | false;
  saveDocumentLoading?: boolean;
  finishedLoading?: boolean;
  documentResetting?: boolean;
  speakers: string[];
  undoStack: DocumentTranscriptV1[];
  redoStack: DocumentTranscriptV1[];
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number;
  selectedParagraphs: number[];
  setSelectedParagraphs: (indices: number[]) => void;
  toggleParagraphSelection: (index: number, multiSelect: boolean) => void;
  clearParagraphSelection: () => void;
  setSearch: (query: string) => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  setCaseSensitive: (caseSensitive: boolean) => void;
  setWholeWord: (wholeWord: boolean) => void;
  setCurrentMatch: (match: DocumentTranscriptTextV1 | null) => void;
  setStartStopWord: (startStopWord: StartStopWord) => void;
  setPopoverOpen: (open: boolean) => void;
  popoverPosition?: { x: number; y: number };
  setPopoverPosition: (position: { x: number; y: number }) => void;
  setShowInfo: (showInfo: boolean) => void;
  setShowJumpButton: (direction: 'up' | 'down' | false) => void;
  saveDocument: (userAction?: boolean) => void;
  handleMediaElementReady: (waveInfoUrl?: string) => void;
  applyStyle: (
    style:
      | 'bold'
      | 'italic'
      | 'underlined'
      | 'color'
      | 'strike'
      | 'mark'
      | 'unmark',
    colorValue?: string | null,
    editor?: ReactEditor
  ) => void;
  addSpeaker: (speaker: string, element: ScriptixDescendant) => void;
  setEditor: (editor: ReactEditor | null) => void;
  updateDocument: (document: DocumentTranscriptV1) => void;
  autoSave: (updatedDocument?: DocumentTranscriptV1) => void;
  finishDocument: (
    sessionId: string,
    documentId: string,
    newStatus: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFinished: (status: boolean, error?: any) => void
  ) => void;
  addUtterance: (isFirstIndex?: boolean) => void;
  deleteUtterance: (index: number) => void;
  currentEditorRef: React.RefObject<ReactEditor | null>;
  splitUtterance: (index: number) => void;
  replaceSpeakers: (oldSpeaker: string, newSpeaker: string) => void;
  editorUpdateParagraph: (children: Descendant[]) => void;
  isDirty?: boolean;
  matches?: DocumentTranscriptTextV1[];
  setMatches?: (m: DocumentTranscriptTextV1[]) => void;
  focusMatch?: (m: DocumentTranscriptTextV1 | null) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  mergeUtterances: (indices: number[]) => void;
}

export interface SharedTranscriptProviderProps {
  children: React.ReactNode;
  session?: Session;
  document?: DocumentResult;
  token?: string;
}

export type State = {
  session?: Session;
  token?: string;
  document?: DocumentTranscriptV1;
  documentResult?: DocumentResult;
  search: string;
  caseSensitive: boolean;
  wholeWord: boolean;
  currentMatch: DocumentTranscriptTextV1 | null;
  startStopWord?: StartStopWord;
  popoverOpen?: boolean;
  popoverPosition?: { x: number; y: number };
  showInfo: boolean;
  clickedComment?: Comment | null;
  showJumpButton: 'up' | 'down' | false;
  speakers: string[];
  isDirty?: boolean;
  matches?: DocumentTranscriptTextV1[];
  undoStack: DocumentTranscriptV1[];
  redoStack: DocumentTranscriptV1[];
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number; // Increments on undo/redo to force editor updates
  selectedParagraphs: number[];
};

export type Action = {
  type:
    | 'SET_DOCUMENT'
    | 'SET_SEARCH'
    | 'SET_START_STOP_WORD'
    | 'SET_POPOVER_OPEN'
    | 'SET_POPOVER_POSITION'
    | 'SET_SHOW_INFO'
    | 'SET_CLICKED_COMMENT'
    | 'SET_JUMP_BUTTON'
    | 'SET_DOCUMENT_RESULT'
    | 'SET_CASE_SENSITIVE'
    | 'SET_WHOLE_WORD'
    | 'SET_CURRENT_MATCH'
    | 'SET_SPEAKERS'
    | 'SET_IS_DIRTY'
    | 'SET_MATCHES'
    | 'SET_UNDO_STACK'
    | 'SET_REDO_STACK'
    | 'SET_SAVE_STATUS'
    | 'SET_LAST_LOCAL_SAVE'
    | 'SET_LAST_SERVER_SYNC'
    | 'INCREMENT_DOCUMENT_VERSION'
    | 'SET_SELECTED_PARAGRAPHS';
  payload:
    | null
    | number
    | boolean
    | string
    | DocumentTranscriptV1
    | Session
    | StartStopWord
    | { x: number; y: number }
    | Comment
    | 'up'
    | 'down'
    | DocumentResult
    | DocumentTranscriptV1[]
    | undefined
    | []
    | DocumentTranscriptTextV1
    | string[]
    | DocumentTranscriptTextV1[]
    | 'unsaved'
    | 'saved-locally'
    | 'synced'
    | 'saving'
    | number[];
};

export const initialState: State = {
  session: undefined,
  token: undefined,
  document: undefined,
  search: '',
  startStopWord: undefined,
  popoverOpen: false,
  popoverPosition: undefined,
  showInfo: false,
  clickedComment: null,
  showJumpButton: false,
  documentResult: undefined,
  caseSensitive: false,
  wholeWord: false,
  currentMatch: null,
  speakers: [],
  isDirty: false,
  undoStack: [],
  redoStack: [],
  saveStatus: 'synced',
  lastLocalSave: null,
  lastServerSync: null,
  documentVersion: 0,
  selectedParagraphs: []
};

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_DOCUMENT':
      return {
        ...state,
        document: action.payload as DocumentTranscriptV1
      };
    case 'SET_SEARCH':
      return {
        ...state,
        search: action.payload as string
      };
    case 'SET_START_STOP_WORD':
      return {
        ...state,
        startStopWord: action.payload as StartStopWord
      };
    case 'SET_POPOVER_OPEN':
      return {
        ...state,
        popoverOpen: Boolean(action.payload)
      };
    case 'SET_POPOVER_POSITION':
      return {
        ...state,
        popoverPosition: action.payload as { x: number; y: number }
      };
    case 'SET_SHOW_INFO':
      return {
        ...state,
        showInfo: Boolean(action.payload)
      };
    case 'SET_CLICKED_COMMENT':
      return {
        ...state,
        clickedComment: action.payload as Comment
      };
    case 'SET_JUMP_BUTTON':
      return {
        ...state,
        showJumpButton: action.payload as 'up' | 'down' | false
      };
    case 'SET_DOCUMENT_RESULT':
      return {
        ...state,
        documentResult: action.payload as DocumentResult
      };
    case 'SET_CASE_SENSITIVE':
      return {
        ...state,
        caseSensitive: Boolean(action.payload)
      };
    case 'SET_WHOLE_WORD':
      return {
        ...state,
        wholeWord: Boolean(action.payload)
      };
    case 'SET_CURRENT_MATCH':
      return {
        ...state,
        currentMatch: !action.payload
          ? null
          : (action.payload as DocumentTranscriptTextV1)
      };
    case 'SET_SPEAKERS':
      return {
        ...state,
        speakers: Array.from(new Set((action.payload as string[]) ?? []))
          .map(s => (s ?? '').trim())
          .filter(Boolean)
      };
    case 'SET_IS_DIRTY':
      return {
        ...state,
        isDirty: Boolean(action.payload)
      };
    case 'SET_MATCHES':
      return {
        ...state,
        matches: (action.payload as DocumentTranscriptTextV1[]) ?? []
      };
    case 'SET_UNDO_STACK':
      return {
        ...state,
        undoStack: action.payload as DocumentTranscriptV1[]
      };
    case 'SET_REDO_STACK':
      return {
        ...state,
        redoStack: action.payload as DocumentTranscriptV1[]
      };
    case 'SET_SAVE_STATUS':
      return {
        ...state,
        saveStatus: action.payload as
          | 'unsaved'
          | 'saved-locally'
          | 'synced'
          | 'saving'
      };
    case 'SET_LAST_LOCAL_SAVE':
      return {
        ...state,
        lastLocalSave: action.payload as number | null
      };
    case 'SET_LAST_SERVER_SYNC':
      return {
        ...state,
        lastServerSync: action.payload as number | null
      };
    case 'INCREMENT_DOCUMENT_VERSION':
      return {
        ...state,
        documentVersion: state.documentVersion + 1
      };
    case 'SET_SELECTED_PARAGRAPHS':
      return {
        ...state,
        selectedParagraphs: (action.payload as number[]) ?? []
      };
    default:
      return state;
  }
}
